import { useMemo, useState } from 'react';
import { ChevronsUpDown } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { SectionLabel } from '../section-label/index.ts';
import {
  collapseContext,
  stableJson,
  toDiffLines,
  type DiffLine,
  type DiffLineKind,
} from './lineDiff.ts';

export interface FieldDiffLabels {
  /** The button that opens a folded run of unchanged lines. */
  expand: (count: number) => string;
  /** Stands in for a value that is not there. */
  empty: string;
}

const DEFAULT_LABELS: FieldDiffLabels = {
  expand: (count) => `Show ${count} more ${count === 1 ? 'line' : 'lines'}`,
  empty: '(empty)',
};

export interface FieldDiffProps {
  before: unknown;
  after: unknown;
  /** Names the field, e.g. "Prompt". Left off when the caller titles it itself. */
  label?: string;
  labels?: Partial<FieldDiffLabels>;
  className?: string;
}

const LINE_TONES: Record<DiffLineKind, string> = {
  add: 'border-success bg-accent-tint text-success',
  remove: 'border-danger-line bg-danger-fill text-danger',
  context: 'border-transparent text-ink-secondary',
};

const GUTTER: Record<DiffLineKind, string> = { add: '+', remove: '-', context: '' };

function isObjectLike(value: unknown): boolean {
  return typeof value === 'object' && value !== null;
}

/** The text a value is diffed as. Objects and arrays go through stable JSON first. */
function toText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';
  return stableJson(value);
}

function DiffLineRow({ line }: { line: DiffLine }) {
  return (
    <div
      data-slot='field-diff-line'
      data-kind={line.kind}
      className={cn(
        'flex gap-2 border-l-2 px-2.5 py-px font-mono text-xs leading-relaxed whitespace-pre-wrap',
        LINE_TONES[line.kind],
      )}
    >
      <span aria-hidden='true' className='w-2 shrink-0 select-none'>
        {GUTTER[line.kind]}
      </span>
      <span className='min-w-0 break-words'>{line.text}</span>
    </div>
  );
}

/**
 * What a field was, and what it became.
 *
 * Written for change logs and for the dialog that asks someone to approve an
 * edit, so it answers "what exactly changed" rather than "what does it say
 * now". Three shapes, because three kinds of value read differently: a
 * paragraph is a line diff, an object is a line diff of its stable JSON — keys
 * sorted, so a re-serialised record does not present itself as an edit — and a
 * number or a name is neither, it is one thing becoming another and belongs on
 * one line.
 *
 * Long unchanged stretches are folded away with three lines of context on each
 * side. A prompt is hundreds of lines and a change to it is usually two; the
 * unfolded version buries the answer in the thing that did not change.
 */
export function FieldDiff({ before, after, label, labels, className }: FieldDiffProps) {
  const [opened, setOpened] = useState<number[]>([]);
  const text = { ...DEFAULT_LABELS, ...labels };

  const scalar =
    !isObjectLike(before) &&
    !isObjectLike(after) &&
    !toText(before).includes('\n') &&
    !toText(after).includes('\n');

  const chunks = useMemo(
    () => (scalar ? [] : collapseContext(toDiffLines(toText(before), toText(after)))),
    [before, after, scalar],
  );

  function formatScalar(value: unknown): string {
    if (value === null || value === undefined || value === '') return text.empty;
    return toText(value);
  }

  return (
    <div data-slot='field-diff' className={cn('flex flex-col gap-1.5', className)}>
      {label !== undefined && <SectionLabel>{label}</SectionLabel>}

      {scalar ? (
        <div
          data-slot='field-diff-scalar'
          className='flex flex-wrap items-baseline gap-x-2 font-mono text-sm'
        >
          {/*
              `del` and `ins` rather than two coloured spans: the colour is the
              whole message here, and a screen reader that cannot see it still
              hears which half was taken away.
            */}
          <del className='break-all text-danger'>{formatScalar(before)}</del>
          <span aria-hidden='true' className='text-ink-muted'>
            →
          </span>
          <ins className='break-all text-success no-underline'>{formatScalar(after)}</ins>
        </div>
      ) : (
        <div
          data-slot='field-diff-lines'
          className='max-h-120 overflow-y-auto rounded-md border border-line bg-surface'
        >
          {chunks.map((chunk, index) =>
            chunk.kind === 'gap' && !opened.includes(index) ? (
              <button
                key={index}
                type='button'
                data-slot='field-diff-expander'
                onClick={() => setOpened((previous) => [...previous, index])}
                className={cn(
                  'flex w-full items-center gap-2 border-y border-line bg-fill-faint px-2.5 py-1.5',
                  'text-xs font-semibold text-ink-muted transition hover:bg-fill hover:text-ink',
                  'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink-faint',
                )}
              >
                <ChevronsUpDown className='size-3 shrink-0' aria-hidden='true' />
                {text.expand(chunk.lines.length)}
              </button>
            ) : (
              chunk.lines.map((line, at) => (
                <DiffLineRow key={`${index}-${at}`} line={line} />
              ))
            ),
          )}
        </div>
      )}
    </div>
  );
}
